/**
 * @fileoverview Scout Compare Page
 * @description Side-by-side movement between two imported Scout runs, laid out like the
 * consolidated report but leading with a Comparison tab instead of the Summary tab.
 * @module features/ScoutEval/pages/ComparePage
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRightLeft, Eye, FileText } from 'lucide-react';
import {
  DeltaIndicator,
  EmptyState,
  ScoreChip,
  ScoutBreadcrumbs,
} from '../components/common';
import { DetailPanel } from '../components/DetailPanel';
import { ReportPanel, ReportTabs } from '../components/ReportTabs';
import { compareRuns } from '../utils/scoutStore';
import { useRunDetails } from '../utils/useRunDetails';
import { MOVEMENT_COLORS, MOVEMENT_LABELS } from '../constants';

const COMPARISON_TAB = 'comparison';

/**
 * Render a labelled run picker.
 * @param {Object} props - Label, runs, selected id, and change handler.
 * @returns {JSX.Element} Run picker.
 */
function RunPicker({ label, runs, value, onChange }) {
  return (
    <Box className="scout-compare-slot">
      <Typography className="scout-metric-label">{label}</Typography>
      <Select
        fullWidth
        size="small"
        className="scout-compare-select"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': label }}
      >
        {[...runs].reverse().map((run) => (
          <MenuItem key={run.id} value={run.id} className="scout-compare-option">
            <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
              <Typography className="scout-compare-run">{run.fileName}</Typography>
              <Typography className="scout-compare-run-date">
                {run.generatedAt || new Date(run.importedAt).toLocaleDateString()}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/**
 * Render a movement tally tile.
 * @param {Object} props - Movement key and count.
 * @returns {JSX.Element} Tally tile.
 */
function TallyTile({ movement, count }) {
  return (
    <Paper className="scout-tally" elevation={0} sx={{ '--tally-color': MOVEMENT_COLORS[movement] }}>
      <Typography className="scout-tally-value">{count}</Typography>
      <Typography className="scout-tally-label">{MOVEMENT_LABELS[movement]}</Typography>
    </Paper>
  );
}

/**
 * Render the run-over-run comparison.
 * @param {Object} props - Stored runs.
 * @returns {JSX.Element} Compare page.
 */
export function ComparePage({ runs }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fallbackCurrent = runs[runs.length - 1]?.id || '';
  const fallbackBaseline = runs[runs.length - 2]?.id || '';

  const [currentId, setCurrentId] = useState(searchParams.get('current') || fallbackCurrent);
  const [baselineId, setBaselineId] = useState(searchParams.get('baseline') || fallbackBaseline);
  const [requestedTab, setRequestedTab] = useState(COMPARISON_TAB);
  const details = useRunDetails(currentId);

  const comparison = useMemo(() => {
    const baseline = runs.find((run) => run.id === baselineId);
    const current = runs.find((run) => run.id === currentId);
    return baseline && current ? compareRuns(baseline, current) : null;
  }, [baselineId, currentId, runs]);

  const tabs = useMemo(() => [
    { id: COMPARISON_TAB, label: 'Comparison' },
    ...(comparison?.groups || []).map((group) => ({ id: group.id, label: group.label })),
  ], [comparison]);

  if (runs.length < 2) {
    return (
      <EmptyState
        icon={ArrowRightLeft}
        title="Import a second report to compare"
        message="Comparison needs at least two Scout reports so every evaluator score can be matched across runs."
      >
        <Button variant="contained" onClick={() => navigate('/scout-eval')}>Back to runs</Button>
      </EmptyState>
    );
  }

  const activeTab = tabs.some((tab) => tab.id === requestedTab) ? requestedTab : COMPARISON_TAB;
  const activeMovement = comparison?.groups.find((group) => group.id === activeTab) || null;
  const currentRun = runs.find((run) => run.id === currentId);
  const activeGroup = currentRun?.groups.find((group) => group.id === activeTab) || activeMovement;

  return (
    <>
      <ScoutBreadcrumbs trail={[{ label: 'Compare runs' }]} />

      <Box className="scout-header">
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box className="scout-header-icon"><ArrowRightLeft size={24} /></Box>
          <Box>
            <Typography variant="h4" component="h1">Compare runs</Typography>
            <Typography color="text.secondary">
              Match every evaluator across two reports to see what improved, what regressed, and what changed shape.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Paper className="scout-panel" elevation={0}>
        <Box className="scout-compare-picker">
          <RunPicker label="Baseline" runs={runs} value={baselineId} onChange={setBaselineId} />
          <Box className="scout-compare-swap">
            <Tooltip title="Swap runs">
              <IconButton
                size="small"
                aria-label="Swap baseline and current"
                onClick={() => {
                  setBaselineId(currentId);
                  setCurrentId(baselineId);
                }}
              >
                <ArrowRightLeft size={16} />
              </IconButton>
            </Tooltip>
          </Box>
          <RunPicker label="Current" runs={runs} value={currentId} onChange={setCurrentId} />
        </Box>
      </Paper>

      {!comparison ? (
        <Paper className="scout-panel" elevation={0}>
          <Box className="scout-no-results">
            <Typography color="text.secondary">Select two different runs to compare.</Typography>
          </Box>
        </Paper>
      ) : (
        <>
          <ReportTabs tabs={tabs} activeId={activeTab} onChange={setRequestedTab} />

          {activeTab === COMPARISON_TAB ? (
            <ReportPanel tabId={COMPARISON_TAB}>
              <Paper className="scout-panel" elevation={0}>
                <Box className="scout-compare-movement">
                  <Paper className="scout-compare-headline" elevation={0}>
                    <Typography className="scout-metric-label">Overall average</Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography className="scout-compare-before">
                        {comparison.summary.overallAverage.before?.toFixed(2) ?? '—'}
                      </Typography>
                      <ArrowRightLeft size={14} />
                      <Typography className="scout-compare-after">
                        {comparison.summary.overallAverage.after?.toFixed(2) ?? '—'}
                      </Typography>
                      <DeltaIndicator delta={comparison.summary.overallAverage.delta} />
                    </Stack>
                    <Typography className="scout-metric-caption">
                      Below target {comparison.summary.belowTarget.before} → {comparison.summary.belowTarget.after}
                      {' · '}
                      Evaluators {comparison.summary.evaluatorsScored.before} → {comparison.summary.evaluatorsScored.after}
                    </Typography>
                  </Paper>
                  <Box className="scout-tally-row">
                    {Object.entries(comparison.tally).map(([movement, count]) => (
                      <TallyTile key={movement} movement={movement} count={count} />
                    ))}
                  </Box>
                </Box>

                <TableContainer className="scout-table-container">
                  <Table size="small" className="scout-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Report - Field</TableCell>
                        <TableCell>AI Evaluator</TableCell>
                        <TableCell align="center">Baseline</TableCell>
                        <TableCell align="center">Current</TableCell>
                        <TableCell align="center">Difference</TableCell>
                        <TableCell align="center"><span className="scout-visually-hidden">Detail</span></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.rows.map((row) => (
                        <TableRow key={row.key} hover className={`scout-row scout-row--${row.afterBand}`}>
                          <TableCell className="scout-cell-field">{row.field}</TableCell>
                          <TableCell>{row.evaluator}</TableCell>
                          <TableCell align="center"><ScoreChip score={row.before} band={row.beforeBand} /></TableCell>
                          <TableCell align="center"><ScoreChip score={row.after} band={row.afterBand} /></TableCell>
                          <TableCell align="center"><DeltaIndicator delta={row.delta} /></TableCell>
                          <TableCell align="center">
                            {tabs.some((tab) => tab.id === row.detailId) && (
                              <Tooltip title="Open detail report">
                                <IconButton
                                  size="small"
                                  className="scout-eye-link"
                                  aria-label={`Open the detail report for ${row.field} ${row.evaluator}`}
                                  onClick={() => setRequestedTab(row.detailId)}
                                >
                                  <Eye size={15} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography className="scout-legend">
                  Movement is measured against the baseline run. Open the eye icon to see per-metric change inside a detail report.
                </Typography>
              </Paper>
            </ReportPanel>
          ) : activeGroup && (
            <ReportPanel tabId={activeGroup.id}>
              <DetailPanel
                group={activeGroup}
                content={details && (details[activeGroup.id] || null)}
                comparison={activeMovement}
                backLabel="Back to comparison"
                onBack={() => setRequestedTab(COMPARISON_TAB)}
              />
            </ReportPanel>
          )}
        </>
      )}

      <Box>
        <Button variant="outlined" startIcon={<FileText size={16} />} onClick={() => navigate('/scout-eval')}>
          Back to runs
        </Button>
      </Box>
    </>
  );
}

export default ComparePage;
